<template>
  <div class="sonar-question">
    <div class="sonar-question__wrapper">
      <div
        v-if="isLabQuestion"
        class="pill pill--secondary sonar-question__lab-pill"
      >
        Lab Question
      </div>
      <div
        ref="question"
        class="sonar-question__content"
        v-html="questionContent"
      ></div>
      <div
        v-if="currentQuestion.hint_html"
        ref="question-hint"
        class="sonar-question__hint"
        v-html="questionHint"
      ></div>
      <div
        v-if="labImagesOnSidebar.length"
        class="sonar-question__images-container"
      >
        <DraggableImage
          v-for="(image, index) in labImagesOnSidebar"
          :key="`li-${randomImageKey(index)}`"
          class="sonar-question__images-container"
          :imgSrc="image.content_id"
          :offsetTop="73"
          :isModal="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CONSTANTS from '@/constants'
import { highlightAllUnder } from 'prismjs'
import '@/assets/css/prism-theme.css'
import 'prismjs/components/prism-python'
import DraggableImage from '@/components/shared/DraggableImage.vue'

const createDOMPurify = require('dompurify')
const DOMPurify = createDOMPurify(window)

export default {
  name: 'SonarQuestion',
  components: {
    DraggableImage,
  },
  props: {
    currentQuestion: {
      type: Object,
      required: true,
    },
    isLabQuestion: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    questionHint() {
      return DOMPurify.sanitize(this.currentQuestion?.hint_html)
    },
    questionContent() {
      return DOMPurify.sanitize(this.currentQuestion.content_html)
    },
    labImagesOnSidebar() {
      const images = this.currentQuestion.modality.filter(
        m => m.content_type === CONSTANTS.CONTENT_TYPES.IMAGE
      )
      return this.currentQuestion.modality.length !== images.length && images
    },
  },
  watch: {
    currentQuestion: {
      immediate: true,
      deep: true,
      handler: 'updatePrism',
    },
  },
  methods: {
    updatePrism() {
      this.$nextTick().then(() => {
        highlightAllUnder(this.$refs.question)
        if (window.MathJax) {
          window.MathJax.Hub.Config({
            tex2jax: {
              inlineMath: [['$', '$']],
              displayMath: [['$$', '$$']],
              processEscapes: true,
              processEnvironments: true,
            },
            // Center justify equations in code and markdown cells. Elsewhere
            // we use CSS to left justify single line equations in code cells.
            displayAlign: 'center',
            'HTML-CSS': {
              styles: { '.MathJax_Display': { margin: 0 } },
              linebreaks: { automatic: true },
            },
          })
          window.MathJax.Hub.Queue([
            'Typeset',
            window.MathJax.Hub,
            this.$refs.question,
          ])
        }
      })
    },
    randomImageKey() {
      return Math.floor(Date.now())
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-question {
  color: var(--white);
  &__wrapper {
    padding: 30px 0 20px;
  }
  &__title {
    font-size: rem(20);
    line-height: rem(24);
    font-family: var(--font-bold);
    margin: 0 0 10px 0;
  }
  &__description {
    font-size: rem(14);
    line-height: rem(20);
    margin-bottom: 30px;
  }
  &__content,
  &__hint {
    color: var(--white);
    font-size: rem(18);
    line-height: rem(24);
    font-weight: 500;
    margin: 0;
    width: 100%;
    word-break: break-word;
    width: 100%;
    box-sizing: border-box;
    flex: 1;
    ::v-deep p {
      margin: 0 0 7px 0;
    }
    ::v-deep.codehilite {
      overflow: auto;
      font-size: 0.95em;
      height: auto;
      display: block;
      width: 100%;
      margin-bottom: 20px;
      margin-top: 20px;
      .token {
        white-space: break-spaces;
      }
    }
  }
  &__images-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  &__hint {
    color: var(--light-blue-gray);
    font-size: rem(14);
    line-height: rem(20);
    padding-left: 15px;
    border-left: 2px solid var(--light-blue-gray);
    margin: 20px 0;
    ::v-deep strong {
      font-size: rem(18);
      line-height: rem(24);
    }
  }
  &__lab-pill {
    width: fit-content;
    margin-bottom: 10px;
  }
}
</style>
